import styled, { DefaultTheme } from "styled-components";
import { Card, Typography, Button, Space, Progress } from "antd";

const { Title, Text } = Typography;

declare module "styled-components" {
  export interface DefaultTheme {
    mode?: "light" | "dark";
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === "dark" ? darkValue : lightValue;
};

export const MainContainer = styled.div`
  padding: 32px;
  color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  background: ${(props) => getThemeValue(props, "#1A202C", "#F7FAFC")} !important;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

export const HeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 32px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const BackButton = styled(Button)`
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 12px rgba(0, 0, 0, 0.3)",
    "0 2px 8px rgba(0, 0, 0, 0.1)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 50%;
  
  &:hover {
    transform: translateX(-1px);
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 6px 16px rgba(0, 0, 0, 0.4)",
      "0 4px 12px rgba(0, 0, 0, 0.15)"
    )};
  }
`;

export const HeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const PageTitle = styled(Title)`
  margin: 0 !important;
  font-weight: 700 !important;
  letter-spacing: -0.5px;
`;

export const PageSubtitle = styled(Text)`
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    align-items: stretch;
  }
`;

export const FilterLabel = styled(Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
  white-space: nowrap;
`;

export const SummaryCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  border-radius: 16px;
  margin-bottom: 24px;
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #1890ff 0%, #722ed1 50%, #fa8c16 100%);
    opacity: 0.8;
    z-index: 1;
  }

  &:hover {
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 8px 30px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)",
      "0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)"
    )};
  }

  .ant-card-body {
    padding: 28px;
  }

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }
`;

export const SummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 16px;
`;

export const SummaryTitle = styled(Text)`
  font-size: 20px;
  font-weight: 700;
`;

export const SummaryDescription = styled(Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
  margin-bottom: 16px;
  display: block;
`;

export const ProfitSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

export const ProfitAmount = styled(Text)`
  font-size: 36px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 28px;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    font-size: 32px;
  }
`;

export const MarginBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const MarginText = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: #52c41a !important;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1025px) and (max-width: 1440px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const MetricCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  border-radius: 12px;
  transition: all 0.2s;
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 2px 8px rgba(0, 0, 0, 0.2)",
    "0 2px 8px rgba(0, 0, 0, 0.06)"
  )};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 8px 24px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)",
      "0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)"
    )};
    border-color: ${(props) => getThemeValue(props, "#4A5568", "rgba(24, 144, 255, 0.3)")};
  }

  .ant-card-body {
    padding: 20px;
  }
`;

export const MetricHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`;

export const MetricIcon = styled.div<{ color: string }>`
  color: ${(props) => props.color};
  font-size: 20px;
  flex-shrink: 0;
`;

export const MetricTitle = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
`;

export const MetricValue = styled(Text)`
  font-size: 24px;
  font-weight: 700;
  margin-top: 8px;
  display: block;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

export const InsightsCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  border-radius: 16px;
  margin-bottom: 24px;
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 8px 30px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)",
      "0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)"
    )};
    transform: translateY(-1px);
  }

  .ant-card-body {
    padding: 28px;
  }
`;

export const InsightsTitle = styled(Text)`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 20px;
  display: block;
`;

export const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const InsightItem = styled.div`
  padding: 16px;
  background: ${(props) => getThemeValue(props, "#4A5568", "#F7FAFC")};
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => getThemeValue(props, "#5A6578", "#EDF2F7")};
  }
`;

export const InsightLabel = styled(Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
  margin-bottom: 8px;
  display: block;
`;

export const InsightProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const InsightProgress = styled(Progress)`
  flex: 1;
`;

export const InsightProgressText = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  min-width: 50px;
`;

export const ChartCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  border-radius: 16px;
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 8px 30px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)",
      "0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)"
    )};
  }

  .ant-card-body {
    padding: 24px;
  }
`;

export const ChartControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const ChartContainer = styled.div`
  width: 100%;
  height: 400px;

  @media (max-width: 768px) {
    height: 320px;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    height: 360px;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 40px;
`;

export const EmptyStateText = styled(Text)`
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
  font-size: 14px;
  text-align: center;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  flex-direction: column;
  gap: 16px;
`;

export const LoadingText = styled(Text)`
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const FullWidthSpace = styled(Space)`
  width: 100%;
`;

export const DateSelectorWrapper = styled.div`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")};
  border-radius: 8px;
  padding: 4px;
`;
